import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertTriangle, CheckCircle, ChevronDown, ChevronRight, X } from "lucide-react";
import Markdown from "react-markdown";
import { EvaluatedSensor, RuleEvaluation, useModeEvaluation } from "@/sync/useModeEvaluation";
import { CollapsibleCard } from "@/opportunity/opportunity_detail_page/CollapsibleCard";
import { Badge } from "@/components/ui/badge";
import React, { useRef, useState } from "react";
import { ToggleGroup, ToggleItem } from "@/components/ui/toggle-group";
import { Button } from "@/components/ui/button";
import { ApiPath } from "@/api/path";
import { TSMetric } from "@/query/query";
import { ChartCard } from "@/components/chart-card";
import { TEN_SECONDS } from "@/dateRanges/refreshIntervals";
import { LAST_1_MINUTE } from "@/dateRanges/dateRanges";
import { ONE_SECOND } from "@/dateRanges/windowSizes";
import { useSensorKitBySerialNumber } from "@/sensorKits/api";
import { ChartRef } from "@/components/chart";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

const RuleEvaluationCard = ({ evaluation }: { evaluation: RuleEvaluation }) => {
  return (
    <Card className={`p-2 flex flex-col gap-2 ${evaluation.is_violation ? 'border-red-200 bg-red-50' : ''}`}>
      <CardContent className="space-y-3">
        <p className="text-sm text-gray-700">{evaluation.explanation}</p>
      </CardContent>
      <hr/>
      <CardFooter className="p-0 flex flex-row gap-2">
        {evaluation.satisfied ? (
          <CheckCircle className="h-5 w-5 text-green-600"/>
        ) : (
          <X className="h-5 w-5 text-red-600"/>
        )}
        <div>
          <span className="font-medium text-gray-600">Confidence:</span>
          <span className="ml-2">{(evaluation.confidence * 100).toFixed(1)}%</span>
        </div>
        {evaluation.target_modes && evaluation.target_modes.length > 0 && (
          <div className="flex flex-row gap-1">
            <span className="font-medium text-gray-600 text-sm">Target Modes:</span>
            <div className="">
              {evaluation.target_modes.map((mode) => (
                <Badge key={mode} variant="secondary" className="text-xs">
                  {mode}
                </Badge>
              ))}
            </div>
          </div>
        )}
        {evaluation.weight && (
          <div>
            <span className="font-medium text-gray-600">Weight:</span>
            <span className="ml-2">{evaluation.weight}</span>
          </div>
        )}
      </CardFooter>
    </Card>
  );
};

const RuleCards = ({ rules }: { rules: RuleEvaluation[] }) => (
  <div className="grid gap-4">
    {rules.map((evaluation, index) => (
      <RuleEvaluationCard
        key={index}
        evaluation={evaluation}
      />
    ))}
  </div>
);

export const RuleEvaluations = ({ evaluations, assumedMode }: {
  evaluations: RuleEvaluation[]
  assumedMode?: string
}) => {
  const violatedRules = evaluations.filter(evaluation => evaluation.is_violation);
  const nonViolatedRules = evaluations.filter(evaluation => !evaluation.is_violation);

  const universalRules = nonViolatedRules.filter(evaluation => !evaluation.target_modes || evaluation.target_modes.length === 0);
  const modeRules = nonViolatedRules.filter(evaluation => evaluation.target_modes && evaluation.target_modes.length > 0);

  return (
    <CollapsibleCard title={"Rule evaluations"}>
      <div className="flex flex-col gap-4">
        {/* Always show violated rules first if any exist */}
        {violatedRules.length > 0 && (
          <div>
            <h4 className="font-semibold mb-2 text-red-600">
              Violated rules ({violatedRules.length})
            </h4>
            <RuleCards rules={violatedRules}/>
          </div>
        )}

        {/* Universal rules */}
        {universalRules.length > 0 && (
          <div>
            <h4 className="font-semibold mb-2">
              Universal rules
            </h4>
            <RuleCards rules={universalRules}/>
          </div>
        )}

        {/* Mode rules - grouped differently based on assumed mode */}
        {assumedMode ? (
          <>
            {(() => {
              const relevantModeRules = modeRules.filter(
                evaluation => evaluation.target_modes && evaluation.target_modes.includes(assumedMode)
              );
              const otherModeRules = modeRules.filter(
                evaluation => !evaluation.target_modes || !evaluation.target_modes.includes(assumedMode)
              );

              return (
                <>
                  {relevantModeRules.length > 0 && (
                    <div>
                      <h4 className="font-semibold mb-2">
                        Rules targeting {assumedMode} mode
                      </h4>
                      <RuleCards rules={relevantModeRules}/>
                    </div>
                  )}

                  {otherModeRules.length > 0 && (
                    <div>
                      <h4 className="font-semibold mb-2">
                        Other mode rules
                      </h4>
                      <RuleCards rules={otherModeRules}/>
                    </div>
                  )}
                </>
              );
            })()}
          </>
        ) : (
          modeRules.length > 0 && (
            <div>
              <h4 className="font-semibold mb-2">
                Mode rules
              </h4>
              <RuleCards rules={modeRules}/>
            </div>
          )
        )}
      </div>
    </CollapsibleCard>
  );
};

export const ModeEvaluationSummary = ({ summary, violatedSensors }: {
  violatedSensors: EvaluatedSensor[]
  summary?: string
}) => {
  const hasViolations = violatedSensors.length > 0

  if (hasViolations) {
    return (
      <Card className="border-red-200 bg-red-50">
        <CardContent className="p-2">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-6 w-6 text-red-600 mt-1 flex-shrink-0"/>
            <div className="flex-1 flex flex-col gap-2">
              <h3 className="font-semibold text-red-800 mb-2">
                {violatedSensors.length} violated sensor{violatedSensors.length !== 1 ? 's' : ''} Found
              </h3>
              {summary && (
                <Markdown
                  components={{
                    li(props) {
                      const { node, ...rest } = props
                      return <li className="list-inside list-disc" {...rest} />
                    }
                  }}
                >
                  {summary}
                </Markdown>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="border-green-200 bg-green-50">
      <CardContent className="pt-6">
        <div className="flex items-center gap-3 justify-center">
          <CheckCircle className="h-6 w-6 text-green-600"/>
          <div className="text-center">
            <h3 className="font-semibold text-green-800">All sensors compliant</h3>
            <p className="text-green-700">No violations detected in the sensor placement validation.</p>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

const EvaluatedSensorsTable = ({ evaluatedSensors }: { evaluatedSensors: EvaluatedSensor[] }) => {
  const [expandedRows, setExpandedRows] = useState(new Set());

  const sortedSensors = [...evaluatedSensors].sort((a, b) => {
    const aInput = a.sensor.temperature_sensor_input_number ?? Infinity;
    const bInput = b.sensor.temperature_sensor_input_number ?? Infinity;
    return aInput - bInput;
  });

  const toggleRow = (sensorDisplayName: string) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(sensorDisplayName)) {
      newExpandedRows.delete(sensorDisplayName);
    } else {
      newExpandedRows.add(sensorDisplayName);
    }
    setExpandedRows(newExpandedRows);
  };

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-12"></TableHead>
          <TableHead>Input Number</TableHead>
          <TableHead>Display Name</TableHead>
          <TableHead>Compliant with assumed mode?</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {sortedSensors.map(evaluatedSensor => {
          const isExpanded = expandedRows.has(evaluatedSensor.sensor.display_name);

          return (
            <Collapsible key={evaluatedSensor.sensor.display_name} open={isExpanded} onOpenChange={() => toggleRow(evaluatedSensor.sensor.display_name)} asChild>
              <>
                <CollapsibleTrigger asChild>
                  <TableRow className={`cursor-pointer hover:bg-gray-50 ${evaluatedSensor.has_violations ? "bg-red-50" : ""}`}>
                    <TableCell>
                      {isExpanded ?
                        <ChevronDown size={16} /> :
                        <ChevronRight size={16} />
                      }
                    </TableCell>
                    <TableCell>{evaluatedSensor.sensor.temperature_sensor_input_number}</TableCell>
                    <TableCell>{evaluatedSensor.sensor.display_name}</TableCell>
                    <TableCell>
                      {evaluatedSensor.has_violations ?
                        <X className="text-red-500" size={20} /> :
                        <CheckCircle className="text-green-500" size={20} />
                      }
                    </TableCell>
                  </TableRow>
                </CollapsibleTrigger>
                <CollapsibleContent asChild>
                  <TableRow>
                    <TableCell colSpan={4} className="p-0">
                      <div className="p-2 bg-gray-50">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Rule Name</TableHead>
                              <TableHead>Explanation</TableHead>
                              <TableHead>Compliant with assumed mode?</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {evaluatedSensor.evaluated_rules
                              .sort((a, b) => a.is_violation === b.is_violation ? 0 : a.is_violation ? -1 : 1)
                              .map((rule, index) => (
                              <TableRow key={`${(evaluatedSensor.sensor.temperature_sensor_input_number)}-${rule.rule_name}-${index}`} className={rule.is_violation ? "bg-red-50" : ""}>
                                <TableCell>{rule.rule_name}</TableCell>
                                <TableCell title={rule.explanation}>
                                  {rule.explanation}
                                </TableCell>
                                <TableCell>
                                  {rule.is_violation ?
                                    <X className="text-red-500" size={16} /> :
                                    <CheckCircle className="text-green-500" size={16} />
                                  }
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </TableCell>
                  </TableRow>
                </CollapsibleContent>
              </>
            </Collapsible>
          );
        })}
      </TableBody>
    </Table>
  );
}

export const SensorKitValidateSensorPlacementsCard = ({ sensorKitSerialNumber }: { sensorKitSerialNumber: string }) => {
  const [assumedMode, setAssumedMode] = useState<string | undefined>(undefined);
  const modeEvaluationMutation = useModeEvaluation();
  const chartRef = useRef<ChartRef>(null);
  const [evaluationTimeWindow, setEvaluationTimeWindow] = useState<{ start: Date; end: Date } | null>(null);

  const { data: sensorKit } = useSensorKitBySerialNumber(sensorKitSerialNumber);

  const handleEvaluate = () => {
    const now = new Date();
    const endTime = chartRef.current ? new Date(chartRef.current.latestDataTimestamp) : now;
    const startTime = new Date(endTime.getTime() - 10000);

    setEvaluationTimeWindow({ start: startTime, end: endTime });

    chartRef.current?.highlightSpecificTimeWindow(
      startTime.getTime(),
      endTime.getTime(),
    );

    modeEvaluationMutation.mutate({
      sensor_kit_serial_number: sensorKitSerialNumber,
      start_time: startTime.toISOString(),
      end_time: endTime.toISOString(),
      assumed_mode: assumedMode
    });
  };

  const formatTimeWindow = () => {
    if (!modeEvaluationMutation.variables) return null;

    const { start_time, end_time } = modeEvaluationMutation.variables;
    const startDate = new Date(start_time);
    const endDate = new Date(end_time);

    return `${startDate.toLocaleTimeString()} - ${endDate.toLocaleTimeString()}`;
  };

  return (
    <Card className="p-2">
      <CardHeader>
        <CardTitle className="text-center">Sensor Kit Validation</CardTitle>
        <p className="text-center text-muted-foreground">{sensorKitSerialNumber}</p>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex flex-row items-end gap-2">
          <div>
            <label className="text-sm font-medium mb-3 block">Assumed Mode:</label>
            <ToggleGroup
              type="single"
              value={assumedMode ?? "none"}
              onValueChange={(value) => setAssumedMode(value === "none" ? undefined : value)}
              className="justify-center"
            >
              <ToggleItem value="none">None</ToggleItem>
              <ToggleItem value="HEATING">Heating</ToggleItem>
              <ToggleItem value="COOLING">Cooling</ToggleItem>
              <ToggleItem value="IDLE">Idle</ToggleItem>
            </ToggleGroup>
          </div>
            <Button
              onClick={handleEvaluate}
              disabled={modeEvaluationMutation.isPending}
              size="sm"
            >
              {modeEvaluationMutation.isPending ? 'Evaluating...' : 'Evaluate last 10s'}
            </Button>
            <Button
              onClick={() => {
                chartRef.current?.clearHighlight();
                setEvaluationTimeWindow(null)
                modeEvaluationMutation.reset();
              }}
              disabled={evaluationTimeWindow == null}
              size="sm"
              variant="destructive"
            >
              Clear
            </Button>
        </div>

        {sensorKit && sensorKit.current_installation && (
          <ChartCard
            chartRef={chartRef}
            className="col-span-12 flex-1 lg:col-span-6"
            title="Refrigerant loop temperatures"
            yAxisName="Temperature"
            height={450}
            unit="°F"
            apiPath={ApiPath.INSTALLATION_GENERIC_LINE_CHART_QUERY}
            query={[
              {
                metrics: [TSMetric.ALL_TEMPERATURES],
                windowSize: ONE_SECOND,
                dateRange: LAST_1_MINUTE,
                organization_id: sensorKit.current_installation.location.organization_id,
                installation_id: [sensorKit.current_installation.id],
                target_unit: "degF"
              },
            ]}
            refreshInterval={TEN_SECONDS}
            canShowTemperatureOnPrimaryYAxis
            clampYAxisToData
          />
        )}

        {modeEvaluationMutation.isError && (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="h-5 w-5"/>
                <span>Error: {modeEvaluationMutation.error.message}</span>
              </div>
            </CardContent>
          </Card>
        )}

        {modeEvaluationMutation.isSuccess && (
          <div className="space-y-4">
            <Card className="p-2">
              <CardContent>
                <div className="text-center space-y-2">
                  <h3 className="font-semibold">Results for {formatTimeWindow()}</h3>
                  <div className="grid grid-cols-2 gap-4 max-w-md mx-auto">
                    <div>
                      <p className="text-sm text-muted-foreground">Assumed Mode</p>
                      <p className="font-medium">{modeEvaluationMutation.data.assumed_mode || 'None'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Detected Mode</p>
                      <p className="font-medium">{modeEvaluationMutation.data.detected_mode}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <EvaluatedSensorsTable evaluatedSensors={modeEvaluationMutation.data.evaluated_sensors}/>

            <ModeEvaluationSummary
              violatedSensors={modeEvaluationMutation.data.evaluated_sensors.filter(evaluatedSensor => evaluatedSensor.has_violations)}
              summary={modeEvaluationMutation.data.summary}
            />

            <RuleEvaluations
              evaluations={modeEvaluationMutation.data.evaluated_rules}
              assumedMode={modeEvaluationMutation.data.assumed_mode}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
};